/**
 * "You already have a reading on your phone."
 *
 * Free-tier letter recordings never leave the device (letterRecordingStorage),
 * so the web app — and any second phone — cannot see them. Whenever a
 * recording is kept, the phone sends the server a note: which letter, how
 * long, when, and the device's name. No audio and no key material. The note
 * is retracted when the recording is deleted, and the server retires it on
 * its own when the recording is uploaded (letter tier+).
 *
 * Every call here is best-effort: a failed note never blocks the recording.
 */
import { createLogger } from '../../utils/logger';
import deviceTrustService from '../deviceTrustService';
import http from '../http';

import { letterRecordingStorage, LetterRecordingMeta } from './letterRecordingStorage';

const logger = createLogger('letterDeviceRecordingNotes');

export interface DeviceRecordingNote {
  id: string;
  letterId: string;
  localId: string;
  kind: 'audio' | 'video';
  durationSecs: number;
  deviceName: string;
  recordedAt: string;
}

/** A recording the owner uploaded (letter tier+), as the server lists it. */
export interface CloudRecordingSummary {
  id: string;
  kind: 'audio' | 'video';
  durationSecs: number;
  createdAt: string;
}

const BASE = '/api/letter-to-loved-ones';

class LetterDeviceRecordingNotes {
  /** Tell the server a recording now exists on this phone. */
  async announce(meta: LetterRecordingMeta): Promise<void> {
    try {
      const deviceName = await deviceTrustService.getDeviceName();
      await http.put(`${BASE}/${meta.letterId}/recordings/on-device/${meta.id}`, {
        kind: meta.kind,
        durationSecs: meta.durationSecs,
        deviceName,
        recordedAt: meta.createdAt,
      });
    } catch (error) {
      logger.debug('On-device note not sent (non-fatal):', error);
    }
  }

  /** The phone deleted the recording; take the note back. */
  async retract(letterId: string, localId: string): Promise<void> {
    try {
      await http.delete(`${BASE}/${letterId}/recordings/on-device/${localId}`);
    } catch (error) {
      logger.debug('On-device note not retracted (non-fatal):', error);
    }
  }

  /**
   * Bring the server's notes in line with what is actually on this phone.
   * A reinstalled app cannot retract the notes of the recordings it lost,
   * and a note can fail to send; so, per visit: announce local recordings
   * the server does not know, and retract notes that name this phone but
   * match nothing here. Notes from other phones are left alone.
   */
  async reconcile(letterId?: string): Promise<void> {
    try {
      const [local, notes, deviceName] = await Promise.all([
        letterRecordingStorage.list(letterId),
        this.list(letterId),
        deviceTrustService.getDeviceName(),
      ]);
      const noted = new Set(notes.map((n) => n.localId));
      const here = new Set(local.map((m) => m.id));
      for (const meta of local) {
        if (!meta.uploadedAt && !noted.has(meta.id)) await this.announce(meta);
      }
      for (const note of notes) {
        if (note.deviceName === deviceName && !here.has(note.localId)) {
          await this.retract(note.letterId, note.localId);
        }
      }
    } catch (error) {
      logger.debug('On-device note reconcile skipped:', error);
    }
  }

  /** Notes for every letter, or for one. Empty on any failure. */
  async list(letterId?: string): Promise<DeviceRecordingNote[]> {
    try {
      const url = letterId
        ? `${BASE}/${letterId}/recordings/on-device`
        : `${BASE}/recordings/on-device`;
      const response = await http.get(url);
      return response.data?.success ? response.data.data ?? [] : [];
    } catch (error) {
      logger.debug('On-device notes unavailable:', error);
      return [];
    }
  }

  /** Uploaded recordings for one letter (letter tier+). Empty on any failure. */
  async listCloud(letterId: string): Promise<CloudRecordingSummary[]> {
    try {
      const response = await http.get(`${BASE}/${letterId}/recordings`);
      return response.data?.success ? response.data.data ?? [] : [];
    } catch (error) {
      logger.debug('Cloud recordings unavailable:', error);
      return [];
    }
  }
}

export const letterDeviceRecordingNotes = new LetterDeviceRecordingNotes();
